import { take, put, all } from "redux-saga/effects";

import { SET_SESSION_DATA, CREATE_SESSION, LOAD_CONFIG, SET_IS_LOADING } from "../../actions/sessionActions";
import { goToModelSelector } from "../../../utilities/navigationUtils";
import {
    SessionInitData,
    SdsAccountInfo,
    BopDisabledFlagMode,
    BumpItem,
    StringMap,
    StringPair,
} from "../../../api/generated/models";

import client from "../../../api/client/SdsWebApiClient";
import {
    getAppVersion,
    getFrontendSettings,
    loadAppCustomization,
    loadConfigurationFiles,
} from "../../../utilities/configUtils";
import Constants from "../../../constants";
import { loadProductInfoSaga } from "./metadataSaga";
import { SummaryConfig } from "../../../models/summary/Config";
import TranslationMap from "../../../models/translationMap";
import { setFilterTypes } from "./filterTypeSaga";
export function* createSessionSaga(): any {
    while (true) {
        const action = yield take(CREATE_SESSION);

        try {
            const { initialConfig, configEncoding, surfaceProductArticleNo } = action;
            if (!initialConfig) {
                throw new Error("initialConfig is not set");
            }

            const clientSettingsPromise = client.getClientSettingsGet();
            const frontendSettingsPromise = getFrontendSettings();
            const appVersionPromise = getAppVersion();

            const searchParams = new URLSearchParams(window.location.search);
            const accountId = searchParams.get(Constants.accountUrlQueryParam);
            let market = searchParams.get(Constants.marketUrlQueryParam) || undefined;
            let language = searchParams.get(Constants.languageUrlQueryParam) || (yield client.getDefaultLanguageGet());
            language = language.toUpperCase();

            const sessionData: SessionInitData = yield client.createSessionPost(
                undefined,
                language,
                market,
                BopDisabledFlagMode.IgnoreDisabledFlag
            );

            let account: SdsAccountInfo | null = null;
            if (accountId) {
                account = yield client.setCurrentSdsAccountPost(sessionData.sessionId, accountId);
            }

            if (!account) {
                account = yield client.getCurrentSdsAccountGet(sessionData.sessionId);
            }

            const configsPromise = loadConfigurationFiles(language, account?.id);

            // Product info is loaded only here (with CREATE_SESSION) and preserved on RESET_FOR_MODEL_SELECTOR,
            // so LOAD_CONFIG never refetches it when reusing the same session.
            const [, [appConfig, summaryData], clientSettings, frontendSettings, appVersion]: [
                any,
                [Object, SummaryConfig],
                StringMap,
                any,
                string
            ] = yield all([
                loadProductInfoSaga(language, sessionData.localization, account?.id),
                configsPromise,
                clientSettingsPromise,
                frontendSettingsPromise,
                appVersionPromise,
            ]);

            // Convert StringMap (array of key/value pairs) to a simple object for easier lookups.
            const applicationSettings: Record<string, string> = {};
            clientSettings.dict.forEach((pair: StringPair) => {
                applicationSettings[pair.key] = pair.value;
            });

            // eslint-disable-next-line no-console
            console.log(`Configurator version: ${appVersion}`);

            let showPrice = applicationSettings["price.enable"] === "True";
            const hidePrice = searchParams.get("hide_price");
            if (
                (hidePrice && /true/i.test(hidePrice)) ||
                frontendSettings.hide_price === true ||
                /true/i.test(frontendSettings.hide_price)
            ) {
                showPrice = false;
            }

            let showFilters = frontendSettings.show_filters;
            const hideFilters = searchParams.get("show_filters");

            if (
                (hideFilters && /false/i.test(hideFilters)) ||
                frontendSettings.show_filters === false ||
                /false/i.test(frontendSettings.show_filters)
            ) {
                showFilters = false;
            } else if (showFilters === undefined && hideFilters && /true/i.test(hideFilters)) {
                showFilters = true;
            }

            let touchScreen = false;
            const isTouchScreen = searchParams.get("touch_screen");

            if (isTouchScreen && /true/i.test(isTouchScreen)) {
                touchScreen = true;
            }

            /* eslint-disable no-param-reassign */
            const guiTranslation = sessionData.bumps[0].items.reduce(
                (translations: { [key: string]: string }, item: BumpItem) => {
                    if (item.shortText) translations[item.id] = item.shortText;
                    return translations;
                },
                {}
            );

            const appCustomization = account ? yield loadAppCustomization(account) : null;
            if (showFilters) {
                yield setFilterTypes(frontendSettings, appCustomization);
            }

            yield put({
                type: SET_SESSION_DATA,
                sessionData,
                frontendSettings,
                appConfig,
                summaryData,
                applicationSettings,
                language,
                showPrice,
                account,
                showFilters,
                guiTranslation: new TranslationMap(guiTranslation),
                appVersion,
                touchScreen,
                appCustomization,
            });

            yield put({
                type: LOAD_CONFIG,
                initialConfig,
                configEncoding,
                surfaceProductArticleNo,
            });
        } catch (e) {
            console.error("[createSessionSaga]", e);
            yield put({ type: SET_IS_LOADING, isLoading: false });
            // Use replace so the failed configurator URL is removed from history and Back won't reload the bad config.
            goToModelSelector(undefined, window.location.search, undefined, { replace: true, forceFullReload: true });
        }
    }
}
